import { createDAppKit } from "@mysten/dapp-kit-react";

import { config } from "@/config";

import { createSuiGrpcClient } from "./grpc";
import { impersonationWalletInitializer } from "./impersonationWallet";
import { getSuiNetwork, SuiNetwork } from "./index";

/**
 * dapp-kit v2 instance.
 *
 * v1 (`SuiClientProvider` + `WalletProvider` + `createNetworkConfig`) was
 * hard-typed to `SuiJsonRpcClient` and is EOL. v2 takes a `createClient`
 * factory typed as `ClientWithCoreApi`, so our `SuiGrpcClient` drops straight in
 * — the wallet itself executes transactions and the client is only used for
 * `Transaction.toJSON()` object/gas resolution, which the core API covers.
 *
 * Clients are created lazily and memoized per network by dapp-kit.
 *
 * SSR: this module is safe to evaluate on the server. `createDAppKit` guards its
 * `localStorage` access and the wallet initializers fail soft without a DOM. Do
 * NOT import `@mysten/dapp-kit-react/ui` from here — that entry registers custom
 * elements and touches `window` at module scope (see `components/wallet/`).
 */
const NETWORKS = ["mainnet", "testnet", "devnet"] satisfies SuiNetwork[];

export const dAppKit = createDAppKit({
  networks: NETWORKS,
  defaultNetwork: getSuiNetwork(),
  createClient: (network) => createSuiGrpcClient(network as SuiNetwork),
  autoConnect: true,
  // DEV-ONLY read-only wallet that "connects" as arbitrary addresses, for
  // checking the dashboard against real staging_v2 records. `enabled` is
  // `import.meta.env.DEV`, so no built bundle ever registers it.
  walletInitializers: config.impersonation.enabled ? [impersonationWalletInitializer()] : [],
});

// Registers the instance type globally so `useCurrentClient()` narrows to
// `SuiGrpcClient` and `useCurrentNetwork()` narrows to our network union,
// instead of falling back to the base `DAppKit<[]>` types.
declare module "@mysten/dapp-kit-react" {
  interface Register {
    dAppKit: typeof dAppKit;
  }
}
